import { toast } from "@components/utils/Toaster";
import { motion } from "framer-motion";
import { cn } from "kz-ui-sdk";
import { useTranslation } from "react-i18next";
import styles from "./PromotionBottomPrompt.module.css";

interface PromotionBottomPromptProps {
  isVisible: boolean;
}

const BOUNCE_DURATION = 2.4;
const FADE_IN_DURATION = 1;
const PROMPT_INITIAL_DELAY = 1;

const BOUNCE_TIMES = [0, 0.12, 0.25, 0.40, 0.62, 0.70, 1];

function PromotionBottomPrompt({ isVisible }: PromotionBottomPromptProps) {
  const { t } = useTranslation();

  const handlePromptClick = () => {
    toast.info(t("click promotion get bonus"), {
      duration: 5000,
    });
  };

  if (!isVisible) return null;

  return (
    <div className="absolute bottom-[70px] left-1/2 -translate-x-1/2">
      <div className="relative flex flex-col items-center">
        <motion.div
          className={cn("trang relative z-10 cursor-pointer rounded-lg p-[0.5px]", styles.promptGradientBorder)}
          onClick={handlePromptClick}
          aria-label="Promotion bottom prompt"
          initial={{ y: 0, opacity: 0 }}
          animate={{
            y: [0, 0, 6, -5, -5, 0, 0],
            opacity: 1,
          }}
          transition={{
            y: {
              duration: BOUNCE_DURATION,
              times: BOUNCE_TIMES,
              ease: "easeInOut",
              repeat: Infinity,
            },
            opacity: {
              duration: FADE_IN_DURATION,
              delay: PROMPT_INITIAL_DELAY,
            },
          }}
        >
          <div
            className={cn(
              "relative flex flex-col items-center whitespace-nowrap rounded-lg px-3 py-1",
              "z-[5] text-xs font-bold uppercase text-[#1a1a1a]",
              styles.promptContent,
            )}
          >
            <div>{t("free bonus")}</div>
            <motion.div
              className="absolute -bottom-[2px] left-[5%] z-20 h-[3px] w-[90%] overflow-hidden rounded-b-lg"
              initial={{ opacity: 0 }}
              animate={{ opacity: [1, 1, 0, 0, 0, 0, 0] }}
              transition={{
                duration: BOUNCE_DURATION,
                times: BOUNCE_TIMES,
                repeat: Infinity,
              }}
            >
              <motion.div
                className={cn("h-full w-[40%]", styles.glowBar)}
                animate={{
                  x: ["-100%", "250%", "250%", "250%", "250%", "-100%", "250%"],
                }}
                transition={{
                  duration: BOUNCE_DURATION,
                  times: BOUNCE_TIMES,
                  ease: "linear",
                  repeat: Infinity,
                }}
              />
            </motion.div>

            <div className="absolute bottom-[-6px] left-1/2 z-[1] h-[12px] w-[12px] -translate-x-1/2 rotate-45">
              <div className="h-full w-full rounded-br-[3px] rounded-tl-[100%] border-b-[0.5px] border-r-[0.5px] border-[#91742B] bg-[#EFDD75]" />
            </div>
          </div>
        </motion.div>

        <motion.img
          src="/images/bg-promotion-prompt.svg"
          alt=""
          className="pointer-events-none absolute -bottom-[33px] left-1/2 z-[5] h-[36px] w-[100px] -translate-x-1/2 -translate-y-1/2 blur-sm"
          initial={{ opacity: 0 }}
          animate={{ opacity: [0, 0, 0, 0, 0.6, 0.6, 0.1] }}
          transition={{
            duration: BOUNCE_DURATION,
            times: BOUNCE_TIMES,
            repeat: Infinity,
          }}
          aria-hidden="true"
        />

        <motion.div
          className={cn(
            "absolute left-1/2 top-[calc(50%-10px)] h-[40px] w-[120px]",
            "z-[4] -translate-x-1/2 -translate-y-1/2 rounded-full blur-xl",
            styles.glowEffect,
          )}
          initial={{ opacity: 0.3 }}
          animate={{ opacity: [0, 0, 0, 0.4, 0.4, 0, 0.3] }}
          transition={{
            duration: BOUNCE_DURATION,
            times: BOUNCE_TIMES,
            repeat: Infinity,
          }}
        />
      </div>
    </div>
  );
}

export { PromotionBottomPrompt };